<script setup>
import { useLayout } from '@/layout/common/composables/layout';
import { useDirection } from '@/composables/useDirection';
import { useLanguageStore } from '@/stores/language';
import { $t, updatePreset, updateSurfacePalette } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import Lara from '@primeuix/themes/lara';
import Nora from '@primeuix/themes/nora';
import { computed, ref, watch, onMounted } from 'vue';

const { layoutConfig, isDarkTheme } = useLayout();
const { isRtl } = useDirection();
const languageStore = useLanguageStore();
const { t } = languageStore;

// Detect system dark mode preference
const systemPrefersDark = ref(false);
if (typeof window !== 'undefined' && window.matchMedia) {
    systemPrefersDark.value = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    // Listen for system theme changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
        systemPrefersDark.value = e.matches;
    });
}

defineProps({
    location: {
        type: String,
        default: 'app'
    },
    simple: {
        type: Boolean,
        default: false
    }
});

const presets = {
    Aura,
    Lara,
    Nora
};
const presetOptions = ref(Object.keys(presets));
const preset = ref(layoutConfig.preset);
const themeOptions = ref([
    { name: 'Light', value: false },
    { name: 'Dark', value: true }
]);
const languageOptions = ref([
    { name: 'English', value: 'en', flag: 'EN' },
    { name: 'العربية', value: 'ar', flag: 'AR' }
]);
const darkTheme = ref(layoutConfig.darkTheme);
const selectedLanguage = ref(languageStore.currentLanguage);
const menuMode = ref(layoutConfig.menuMode);
const menuTheme = computed(() => layoutConfig.menuTheme);

const primaryColors = ref([
    {
        name: 'moi-medical',
        palette: {
            50: '#eefafa', 100: '#d6f3f4', 200: '#afe7e9', 300: '#78d5d8', 400: '#42bec2',
            500: '#22a9ae', 600: '#168c91', 700: '#11777b', 800: '#0e6064', 900: '#0b4b4e', 950: '#062f31'
        },
        gradient: 'linear-gradient(135deg, #168c91, #22a9ae)',
        displayColor: '#22a9ae'
    },
    // Legacy palettes remain defined for safe migration, but are not exposed.
    { 
        name: 'emerald-forest', 
        palette: { 
            50: '#e8f5f3', 100: '#c6e7e2', 200: '#a0d8d0', 300: '#7ac9be', 400: '#5ebeaf', 
            500: '#3F8782', 600: '#2e6e6a', 700: '#235854', 800: '#1a433f', 900: '#163933', 950: '#0d2320' 
        },
        gradient: 'linear-gradient(to bottom, #163933, #3F8782)',
        displayColor: '#3F8782'
    },
    { 
        name: 'ocean-depths', 
        palette: { 
            50: '#e6f0f5', 100: '#bfd8e6', 200: '#94bed6', 300: '#69a4c6', 400: '#4990ba', 
            500: '#305865', 600: '#1f4a57', 700: '#163d4a', 800: '#0f303d', 900: '#0a2530', 950: '#051419' 
        },
        gradient: 'linear-gradient(to bottom, #003C56, #305865)',
        displayColor: '#305865'
    },
    { 
        name: 'azure-sky', 
        palette: { 
            50: '#e8f2fa', 100: '#c5def3', 200: '#9ec9ec', 300: '#77b4e5', 400: '#5ca5df', 
            500: '#45759F', 600: '#366490', 700: '#2b5278', 800: '#214161', 900: '#1a334c', 950: '#0f1d2b' 
        },
        gradient: 'linear-gradient(to bottom, #4287BB, #45759F)',
        displayColor: '#45759F'
    },
    { 
        name: 'golden-earth', 
        palette: { 
            50: '#f5f0e8', 100: '#e6d9c4', 200: '#d6bf9d', 300: '#c6a576', 400: '#ba925a', 
            500: '#5A5A4A', 600: '#4d4d3f', 700: '#3f3f34', 800: '#31312a', 900: '#25251f', 950: '#151511' 
        },
        gradient: 'linear-gradient(to bottom, #D2923F, #C48A42, #5A5A4A)',
        displayColor: '#C48A42'
    },
    { 
        name: 'royal-wine', 
        palette: { 
            50: '#fce8ec', 100: '#f7c6d0', 200: '#f0a0b1', 300: '#e87a92', 400: '#e25e7c', 
            500: '#9B304A', 600: '#8a2741', 700: '#741f36', 800: '#5f182b', 900: '#4a1220', 950: '#2d0b13' 
        },
        gradient: 'linear-gradient(180deg, #9B304A 0%, #4E2D73 100%)',
        displayColor: '#9B304A'
    },
    { 
        name: 'majestic-purple', 
        palette: { 
            50: '#f0e9f5', 100: '#d9c8e6', 200: '#c0a3d6', 300: '#a77ec6', 400: '#9363ba', 
            500: '#4E2D73', 600: '#432563', 700: '#371e51', 800: '#2c1840', 900: '#21122f', 950: '#140b1d' 
        },
        gradient: 'linear-gradient(180deg, #4E2D73 0%, #2C1E3F 100%)',
        displayColor: '#4E2D73'
    },
    { 
        name: 'midnight-navy', 
        palette: { 
            50: '#e8f2f5', 100: '#c6dfe6', 200: '#a0cad6', 300: '#7ab5c6', 400: '#5ea5ba', 
            500: '#1e3a52', 600: '#193048', 700: '#14263a', 800: '#0f1d2d', 900: '#0b1521', 950: '#060d14' 
        },
        gradient: 'linear-gradient(180deg, #1e3a52 0%, #0f1d2d 50%, #1e3a52 100%)',
        displayColor: '#1e3a52'
    },
    { 
        name: 'twilight-violet', 
        palette: { 
            50: '#ede8f2', 100: '#d3c6df', 200: '#b7a0ca', 300: '#9b7ab5', 400: '#865ea5', 
            500: '#3d2451', 600: '#341f45', 700: '#2a1938', 800: '#21142c', 900: '#180f20', 950: '#0f0913' 
        },
        gradient: 'linear-gradient(180deg, #4E2D73 0%, #3d2451 50%, #2a1938 100%)',
        displayColor: '#3d2451'
    },
    // { name: 'noir', palette: {} },
    // { name: 'emerald', palette: { 50: '#ecfdf5', 100: '#d1fae5', 200: '#a7f3d0', 300: '#6ee7b7', 400: '#34d399', 500: '#10b981', 600: '#059669', 700: '#047857', 800: '#065f46', 900: '#064e3b', 950: '#022c22' } },
    // { name: 'green', palette: { 50: '#f0fdf4', 100: '#dcfce7', 200: '#bbf7d0', 300: '#86efac', 400: '#4ade80', 500: '#22c55e', 600: '#16a34a', 700: '#15803d', 800: '#166534', 900: '#14532d', 950: '#052e16' } },
    // { name: 'lime', palette: { 50: '#f7fee7', 100: '#ecfccb', 200: '#d9f99d', 300: '#bef264', 400: '#a3e635', 500: '#84cc16', 600: '#65a30d', 700: '#4d7c0f', 800: '#3f6212', 900: '#365314', 950: '#1a2e05' } },
    // { name: 'orange', palette: { 50: '#fff7ed', 100: '#ffedd5', 200: '#fed7aa', 300: '#fdba74', 400: '#fb923c', 500: '#f97316', 600: '#ea580c', 700: '#c2410c', 800: '#9a3412', 900: '#7c2d12', 950: '#431407' } },
    // { name: 'amber', palette: { 50: '#fffbeb', 100: '#fef3c7', 200: '#fde68a', 300: '#fcd34d', 400: '#fbbf24', 500: '#f59e0b', 600: '#d97706', 700: '#b45309', 800: '#92400e', 900: '#78350f', 950: '#451a03' } },
    // { name: 'yellow', palette: { 50: '#fefce8', 100: '#fef9c3', 200: '#fef08a', 300: '#fde047', 400: '#facc15', 500: '#eab308', 600: '#ca8a04', 700: '#a16207', 800: '#854d0e', 900: '#713f12', 950: '#422006' } },
    // { name: 'teal', palette: { 50: '#f0fdfa', 100: '#ccfbf1', 200: '#99f6e4', 300: '#5eead4', 400: '#2dd4bf', 500: '#14b8a6', 600: '#0d9488', 700: '#0f766e', 800: '#115e59', 900: '#134e4a', 950: '#042f2e' } },
    // { name: 'cyan', palette: { 50: '#ecfeff', 100: '#cffafe', 200: '#a5f3fc', 300: '#67e8f9', 400: '#22d3ee', 500: '#06b6d4', 600: '#0891b2', 700: '#0e7490', 800: '#155e75', 900: '#164e63', 950: '#083344' } },
    // { name: 'sky', palette: { 50: '#f0f9ff', 100: '#e0f2fe', 200: '#bae6fd', 300: '#7dd3fc', 400: '#38bdf8', 500: '#0ea5e9', 600: '#0284c7', 700: '#0369a1', 800: '#075985', 900: '#0c4a6e', 950: '#082f49' } },
    // { name: 'blue', palette: { 50: '#eff6ff', 100: '#dbeafe', 200: '#bfdbfe', 300: '#93c5fd', 400: '#60a5fa', 500: '#3b82f6', 600: '#2563eb', 700: '#1d4ed8', 800: '#1e40af', 900: '#1e3a8a', 950: '#172554' } },
    // { name: 'indigo', palette: { 50: '#eef2ff', 100: '#e0e7ff', 200: '#c7d2fe', 300: '#a5b4fc', 400: '#818cf8', 500: '#6366f1', 600: '#4f46e5', 700: '#4338ca', 800: '#3730a3', 900: '#312e81', 950: '#1e1b4b' } },
    // { name: 'violet', palette: { 50: '#f5f3ff', 100: '#ede9fe', 200: '#ddd6fe', 300: '#c4b5fd', 400: '#a78bfa', 500: '#8b5cf6', 600: '#7c3aed', 700: '#6d28d9', 800: '#5b21b6', 900: '#4c1d95', 950: '#2e1065' } },
    // { name: 'purple', palette: { 50: '#faf5ff', 100: '#f3e8ff', 200: '#e9d5ff', 300: '#d8b4fe', 400: '#c084fc', 500: '#a855f7', 600: '#9333ea', 700: '#7e22ce', 800: '#6b21a8', 900: '#581c87', 950: '#3b0764' } },
    // { name: 'fuchsia', palette: { 50: '#fdf4ff', 100: '#fae8ff', 200: '#f5d0fe', 300: '#f0abfc', 400: '#e879f9', 500: '#d946ef', 600: '#c026d3', 700: '#a21caf', 800: '#86198f', 900: '#701a75', 950: '#4a044e' } },
    // { name: 'pink', palette: { 50: '#fdf2f8', 100: '#fce7f3', 200: '#fbcfe8', 300: '#f9a8d4', 400: '#f472b6', 500: '#ec4899', 600: '#db2777', 700: '#be185d', 800: '#9d174d', 900: '#831843', 950: '#500724' } },
    // { name: 'rose', palette: { 50: '#fff1f2', 100: '#ffe4e6', 200: '#fecdd3', 300: '#fda4af', 400: '#fb7185', 500: '#f43f5e', 600: '#e11d48', 700: '#be123c', 800: '#9f1239', 900: '#881337', 950: '#4c0519' } },
    // { name: 'brand-blue', palette: { 50: '#f0f4ff', 100: '#dde6ff', 200: '#c2d4ff', 300: '#9cbfff', 400: '#6ba2ff', 500: '#3b82ff', 600: '#2563eb', 700: '#1d4ed8', 800: '#1e40af', 900: '#1e3a8a', 950: '#172554' } },
    // { name: 'brand-purple', palette: { 50: '#faf5ff', 100: '#f3e8ff', 200: '#e9d5ff', 300: '#d8b4fe', 400: '#c084fc', 500: '#a855f7', 600: '#9333ea', 700: '#7e22ce', 800: '#6b21a8', 900: '#581c87', 950: '#3b0764' } },
].filter((color) => color.name === 'moi-medical'));

const surfaces = ref([
    {
        name: 'slate',
        palette: { 0: '#ffffff', 50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 300: '#cbd5e1', 400: '#94a3b8', 500: '#64748b', 600: '#475569', 700: '#334155', 800: '#1e293b', 900: '#0f172a', 950: '#020617' }
    },
    {
        name: 'gray',
        palette: { 0: '#ffffff', 50: '#f9fafb', 100: '#f3f4f6', 200: '#e5e7eb', 300: '#d1d5db', 400: '#9ca3af', 500: '#6b7280', 600: '#4b5563', 700: '#374151', 800: '#1f2937', 900: '#111827', 950: '#030712' }
    },
    {
        name: 'zinc',
        palette: { 0: '#ffffff', 50: '#fafafa', 100: '#f4f4f5', 200: '#e4e4e7', 300: '#d4d4d8', 400: '#a1a1aa', 500: '#71717a', 600: '#52525b', 700: '#3f3f46', 800: '#27272a', 900: '#18181b', 950: '#09090b' }
    },
    {
        name: 'neutral',
        palette: { 0: '#ffffff', 50: '#fafafa', 100: '#f5f5f5', 200: '#e5e5e5', 300: '#d4d4d4', 400: '#a3a3a3', 500: '#737373', 600: '#525252', 700: '#404040', 800: '#262626', 900: '#171717', 950: '#0a0a0a' }
    },
    {
        name: 'stone',
        palette: { 0: '#ffffff', 50: '#fafaf9', 100: '#f5f5f4', 200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c', 600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917', 950: '#0c0a09' }
    },
    {
        name: 'soho',
        palette: { 0: '#ffffff', 50: '#f4f4f4', 100: '#e8e9e9', 200: '#d2d2d4', 300: '#bbbcbe', 400: '#a5a5a9', 500: '#8e8f93', 600: '#77787d', 700: '#616268', 800: '#4a4b52', 900: '#34343d', 950: '#1d1e27' }
    },
    {
        name: 'viva',
        palette: { 0: '#ffffff', 50: '#f3f3f3', 100: '#e7e7e8', 200: '#cfd0d0', 300: '#b7b8b9', 400: '#9fa1a1', 500: '#87898a', 600: '#6e7173', 700: '#565a5b', 800: '#3e4244', 900: '#262b2c', 950: '#0e1315' }
    },
    {
        name: 'ocean',
        palette: { 0: '#ffffff', 50: '#fbfcfc', 100: '#F7F9F8', 200: '#EFF3F2', 300: '#DADEDD', 400: '#B1B7B6', 500: '#828787', 600: '#5F7274', 700: '#415B61', 800: '#29444E', 900: '#183240', 950: '#0c1920' }
    }
]);

function executeDarkModeToggle() {
    layoutConfig.darkTheme = !layoutConfig.darkTheme;
    
    // Apply dark mode class to document
    if (layoutConfig.darkTheme) {
        document.documentElement.classList.add('app-dark');
    } else {
        document.documentElement.classList.remove('app-dark');
    }
    
    // Reapply the current theme to ensure colors are updated for dark mode
    const currentPrimaryColor = primaryColors.value.find((c) => c.name === layoutConfig.primary);
    if (currentPrimaryColor) {
        applyTheme('primary', currentPrimaryColor);
    }
    
    // Update surface palette for dark mode
    const surfacePalette = surfaces.value.find((s) => s.name === layoutConfig.surface)?.palette;
    if (surfacePalette) {
        updateSurfacePalette(surfacePalette);
    }
}

function handleLanguageChange() {
    languageStore.setLanguage(selectedLanguage.value);
}

function handleToggleDarkMode() {
    if (!document.startViewTransition) {
        executeDarkModeToggle();

        return;
    }

    document.startViewTransition(() => {
        executeDarkModeToggle();
    });
}

function updateColors(type, color) {
    if (type === 'primary') {
        layoutConfig.primary = color.name;
    } else if (type === 'surface') {
        layoutConfig.surface = color.name;
    }

    applyTheme(type, color);
}

function hexToRgb(hex) {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Parse hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    return { r, g, b };
}

function applyTheme(type, color) {
    if (type === 'primary') {
        updatePreset(getPresetExt());

        if (color.name === 'noir') {
            document.documentElement.classList.add('p-noir');
        } else {
            document.documentElement.classList.remove('p-noir');
        }
        
        // Update CSS variables for gradients if the selected color has a gradient
        if (color.gradient) {
            document.documentElement.style.setProperty('--gradient-1', color.gradient);
            document.documentElement.style.setProperty('--primary-gradient', color.gradient);
            
            // The application may expose palette choices, but the launch screen
            // remains on the approved MOI teal-and-gold brand system.
            const preloaderBg = layoutConfig.darkTheme
                ? 'radial-gradient(circle at 78% 12%, rgba(181,154,99,.16), transparent 28%), linear-gradient(135deg, #07191b 0%, #0d3033 55%, #124f54 100%)'
                : 'radial-gradient(circle at 78% 12%, rgba(216,191,131,.24), transparent 28%), linear-gradient(135deg, #0d2f32 0%, #168c91 58%, #22a9ae 100%)';
            document.documentElement.style.setProperty('--preloader-bg', preloaderBg);
            document.documentElement.style.setProperty('--preloader-accent', '#d8bf83');
            
            // Update button colors to use the selected gradient
            document.documentElement.style.setProperty('--button-primary-bg', color.gradient);
            
            // For dark mode, create a slightly lighter version of the gradient for better visibility
            if (layoutConfig.darkTheme) {
                // Use the gradient with adjusted opacity or the original gradient
                document.documentElement.style.setProperty('--button-primary-hover', color.gradient);
            }
        }
        
        // Update primary color CSS variable
        if (color.displayColor || color.palette['500']) {
            const primaryColor = color.displayColor || color.palette['500'];
            document.documentElement.style.setProperty('--primary-color', primaryColor);
            
            // For dark mode, use a lighter shade from the palette
            if (layoutConfig.darkTheme && color.palette['400']) {
                document.documentElement.style.setProperty('--primary-color-dark', color.palette['400']);
            }
        }
        
        // Update all primary-related CSS variables
        if (color.palette) {
            // Set the full palette for advanced customization
            Object.keys(color.palette).forEach(shade => {
                document.documentElement.style.setProperty(`--primary-${shade}`, color.palette[shade]);
            });
            
            // Set interactive state colors based on light/dark mode
            if (layoutConfig.darkTheme) {
                // Dark mode: use lighter shades for interactive states
                document.documentElement.style.setProperty('--primary-hover', color.palette['400'] || color.palette['500']);
                document.documentElement.style.setProperty('--primary-active', color.palette['300'] || color.palette['400']);
                document.documentElement.style.setProperty('--primary-focus', color.palette['400'] || color.palette['500']);
                document.documentElement.style.setProperty('--primary-before', color.palette['500'] || color.palette['600']);
                document.documentElement.style.setProperty('--primary-after', color.palette['400'] || color.palette['500']);
            } else {
                // Light mode: use darker shades for interactive states
                document.documentElement.style.setProperty('--primary-hover', color.palette['600'] || color.palette['500']);
                document.documentElement.style.setProperty('--primary-active', color.palette['700'] || color.palette['600']);
                document.documentElement.style.setProperty('--primary-focus', color.palette['600'] || color.palette['500']);
                document.documentElement.style.setProperty('--primary-before', color.palette['500'] || color.palette['600']);
                document.documentElement.style.setProperty('--primary-after', color.palette['600'] || color.palette['500']);
            }
            
            // Set RGB values for primary colors (for rgba usage)
            if (color.palette['900']) {
                const rgb900 = hexToRgb(color.palette['900']);
                if (rgb900) {
                    document.documentElement.style.setProperty('--primary-900-rgb', `${rgb900.r}, ${rgb900.g}, ${rgb900.b}`);
                }
            }
            if (color.palette['800']) {
                const rgb800 = hexToRgb(color.palette['800']);
                if (rgb800) {
                    document.documentElement.style.setProperty('--primary-800-rgb', `${rgb800.r}, ${rgb800.g}, ${rgb800.b}`);
                }
            }
        }
        
        // Set success and danger color variables (for statistics cards)
        // Success (Green) colors
        document.documentElement.style.setProperty('--success-50', '#ecfdf5');
        document.documentElement.style.setProperty('--success-100', '#d1fae5');
        document.documentElement.style.setProperty('--success-500', '#10b981');
        document.documentElement.style.setProperty('--success-600', '#059669');
        document.documentElement.style.setProperty('--success-700', '#047857');
        document.documentElement.style.setProperty('--success-800', '#065f46');
        document.documentElement.style.setProperty('--success-900', '#064e3b');
        
        // Danger (Red) colors
        document.documentElement.style.setProperty('--danger-50', '#fef2f2');
        document.documentElement.style.setProperty('--danger-100', '#fee2e2');
        document.documentElement.style.setProperty('--danger-500', '#ef4444');
        document.documentElement.style.setProperty('--danger-600', '#dc2626');
        document.documentElement.style.setProperty('--danger-700', '#b91c1c');
        document.documentElement.style.setProperty('--danger-800', '#991b1b');
        document.documentElement.style.setProperty('--danger-900', '#7f1d1d');
        
        // Info (Blue) colors
        document.documentElement.style.setProperty('--info-50', '#eff6ff');
        document.documentElement.style.setProperty('--info-100', '#dbeafe');
        document.documentElement.style.setProperty('--info-500', '#3b82f6');
        document.documentElement.style.setProperty('--info-600', '#2563eb');
        document.documentElement.style.setProperty('--info-700', '#1d4ed8');
        document.documentElement.style.setProperty('--info-800', '#1e40af');
        document.documentElement.style.setProperty('--info-900', '#1e3a8a');
        
        // Warning (Orange) colors
        document.documentElement.style.setProperty('--warning-50', '#fff7ed');
        document.documentElement.style.setProperty('--warning-100', '#ffedd5');
        document.documentElement.style.setProperty('--warning-500', '#f97316');
        document.documentElement.style.setProperty('--warning-600', '#ea580c');
        document.documentElement.style.setProperty('--warning-700', '#c2410c');
        document.documentElement.style.setProperty('--warning-800', '#9a3412');
        document.documentElement.style.setProperty('--warning-900', '#7c2d12');
    } else if (type === 'surface') {
        updateSurfacePalette(color.palette);
    }
}

function onPresetChange() {
    layoutConfig.preset = preset.value;
    const presetValue = presets[preset.value];
    const surfacePalette = surfaces.value.find((s) => s.name === layoutConfig.surface)?.palette;

    $t().preset(presetValue).preset(getPresetExt()).surfacePalette(surfacePalette).use({ useDefaultOptions: true });
}

const handleSetMenuMode = (mode) => {
    const supportedMode = mode === 'horizontal' ? 'static' : mode;
    layoutConfig.menuMode = supportedMode;
    menuMode.value = supportedMode;

    if (supportedMode === 'static') {
        layoutConfig.menuState = 'expanded';
    }
};

function getPresetExt() {
    const color = primaryColors.value.find((c) => c.name === layoutConfig.primary);
    
    // The application uses one approved MOI primary palette.
    if (!color) {
        layoutConfig.primary = 'moi-medical';
        return getPresetExt();
    }

    if (color.name === 'noir') {
        return {
            semantic: {
                primary: {
                    50: '{surface.50}',
                    100: '{surface.100}',
                    200: '{surface.200}',
                    300: '{surface.300}',
                    400: '{surface.400}',
                    500: '{surface.500}',
                    600: '{surface.600}',
                    700: '{surface.700}',
                    800: '{surface.800}',
                    900: '{surface.900}',
                    950: '{surface.950}'
                },
                colorScheme: {
                    light: {
                        primary: {
                            color: '{primary.950}',
                            contrastColor: '#ffffff',
                            hoverColor: '{primary.800}',
                            activeColor: '{primary.700}'
                        },
                        highlight: {
                            background: '{primary.950}',
                            focusBackground: '{primary.700}',
                            color: '#ffffff',
                            focusColor: '#ffffff'
                        }
                    },
                    dark: {
                        primary: {
                            color: '{primary.50}',
                            contrastColor: '{primary.950}',
                            hoverColor: '{primary.200}',
                            activeColor: '{primary.300}'
                        },
                        highlight: {
                            background: '{primary.50}',
                            focusBackground: '{primary.300}',
                            color: '{primary.950}',
                            focusColor: '{primary.950}'
                        }
                    }
                }
            }
        };
    } else {
        return {
            semantic: {
                primary: color.palette,
                colorScheme: {
                    light: {
                        primary: {
                            color: '{primary.500}',
                            contrastColor: '#ffffff',
                            hoverColor: '{primary.600}',
                            activeColor: '{primary.700}'
                        },
                        highlight: {
                            background: '{primary.50}',
                            focusBackground: '{primary.100}',
                            color: '{primary.700}',
                            focusColor: '{primary.800}'
                        }
                    },
                    dark: {
                        primary: {
                            color: '{primary.400}',
                            contrastColor: '{surface.900}',
                            hoverColor: '{primary.300}',
                            activeColor: '{primary.200}'
                        },
                        highlight: {
                            background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
                            focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
                            color: 'rgba(255,255,255,.87)',
                            focusColor: 'rgba(255,255,255,.87)'
                        }
                    }
                }
            }
        };
    }
}

watch(isDarkTheme, (newValue) => {
    if (newValue && menuTheme.value === 'light') {
        layoutConfig.menuTheme = 'dark';
    }
    
    // Reapply theme when dark mode changes to ensure proper colors
    let currentPrimaryColor = primaryColors.value.find((c) => c.name === layoutConfig.primary);
    
    // Migrate saved legacy colors to the approved MOI palette.
    if (!currentPrimaryColor) {
        layoutConfig.primary = 'moi-medical';
        currentPrimaryColor = primaryColors.value.find((c) => c.name === 'moi-medical');
    }
    
    if (currentPrimaryColor) {
        applyTheme('primary', currentPrimaryColor);
    }
    
    // Apply/remove dark mode class
    if (newValue) {
        document.documentElement.classList.add('app-dark');
    } else {
        document.documentElement.classList.remove('app-dark');
    }
});

// Watch for primary color changes and reapply theme
watch(() => layoutConfig.primary, (newValue) => {
    let color = primaryColors.value.find((c) => c.name === newValue);
    
    // Migrate saved legacy colors to the approved MOI palette.
    if (!color) {
        layoutConfig.primary = 'moi-medical';
        color = primaryColors.value.find((c) => c.name === 'moi-medical');
    }
    
    if (color) {
        applyTheme('primary', color);
    }
});

// Watch for surface color changes
watch(() => layoutConfig.surface, (newValue) => {
    const surface = surfaces.value.find((s) => s.name === newValue);
    if (surface) {
        updateSurfacePalette(surface.palette);
    }
});

// Initialize the theme with the default primary color on component mount
onMounted(() => {
    if (layoutConfig.menuMode === 'horizontal') {
        layoutConfig.menuMode = 'static';
        layoutConfig.menuState = 'expanded';
        menuMode.value = 'static';
    }

    // Apply dark mode class if needed
    if (layoutConfig.darkTheme) {
        document.documentElement.classList.add('app-dark');
    } else {
        document.documentElement.classList.remove('app-dark');
    }
    
    let defaultPrimaryColor = primaryColors.value.find((c) => c.name === layoutConfig.primary);
    
    // Migrate saved legacy colors to the approved MOI palette.
    if (!defaultPrimaryColor) {
        layoutConfig.primary = 'moi-medical';
        defaultPrimaryColor = primaryColors.value.find((c) => c.name === 'moi-medical');
    }
    
    if (defaultPrimaryColor) {
        applyTheme('primary', defaultPrimaryColor);
    }
    
    // Apply the saved preset
    const presetValue = presets[layoutConfig.preset];
    const surfacePalette = surfaces.value.find((s) => s.name === layoutConfig.surface)?.palette;
    $t().preset(presetValue).preset(getPresetExt()).surfacePalette(surfacePalette).use({ useDefaultOptions: true });
});
</script>

<template>
    <Drawer
        v-model:visible="layoutConfig.configSidebarVisible"
        :position="isRtl ? 'left' : 'right'"
        :header="t('Settings')"
        class="layout-config-sidebar w-80"
        :pt="{
            pcCloseButton: { 
                root: isRtl ? 'absolute left-4' : 'absolute right-4'
            }
        }"
    >
        <div class="flex flex-col gap-6">
            <div>
                <span class="text-lg text-muted-color font-semibold">{{ t('Primary') }}</span>
                <div class="pt-2 flex gap-2 flex-wrap">
                    <button
                        v-for="primaryColor of primaryColors"
                        :key="primaryColor.name"
                        type="button"
                        disabled
                        :aria-label="t('MOI primary color')"
                        class="w-6 h-6 cursor-default border border-surface rounded duration-150 flex items-center justify-center"
                        :style="{ 
                            background: primaryColor.gradient 
                                ? primaryColor.gradient 
                                : (primaryColor.name === 'noir' ? 'var(--text-color)' : primaryColor.palette['500'])
                        }"
                    >
                        <i v-if="primaryColor.name === layoutConfig.primary" class="pi pi-check text-white" />
                    </button>
                </div>
            </div>

            <!-- <div>
                <span class="text-lg text-muted-color font-semibold">Surface</span>
                <div class="pt-2 flex gap-2 flex-wrap">
                    <button
                        v-for="surface of surfaces"
                        :key="surface.name"
                        type="button"
                        @click="updateColors('surface', surface)"
                        class="w-6 h-6 cursor-pointer hover:shadow-lg rounded duration-150 flex items-center justify-center"
                        :style="{ backgroundColor: `${surface.palette['500']}` }"
                    >
                        <i v-if="layoutConfig.surface ? layoutConfig.surface === surface.name : isDarkTheme ? surface.name === 'zinc' : surface.name === 'slate'" class="pi pi-check text-white" />
                    </button>
                </div>
            </div> -->

            <div>
                <div class="flex flex-col gap-2">
                    <span class="text-lg text-muted-color font-semibold">{{ t('Presets') }}</span>
                    <SelectButton v-model="preset" @change="onPresetChange" :options="presetOptions" :allowEmpty="false" />
                </div>
            </div>

            <div>
                <div class="flex flex-col gap-2">
                    <span class="text-lg text-muted-color font-semibold">{{ t('Color Scheme') }}</span>
                    <SelectButton v-model="darkTheme" @change="handleToggleDarkMode" :options="themeOptions" optionLabel="name" optionValue="value" :allowEmpty="false" />
                </div>
            </div>

            <div>
                <div class="flex flex-col gap-2">
                    <span class="text-lg text-muted-color font-semibold">{{ t('Language') }}</span>
                    <SelectButton v-model="selectedLanguage" @change="handleLanguageChange" :options="languageOptions" optionLabel="flag" optionValue="value" :allowEmpty="false" />
                </div>
            </div>

            <div v-if="!simple && location === 'app'">
                <div class="flex flex-col gap-2">
                    <span class="text-lg text-muted-color font-semibold">{{ t('Menu Type') }}</span>
                    <div class="flex flex-wrap flex-col gap-3">
                        <div class="flex">
                            <div class="flex items-center gap-2 w-6/12">
                                <RadioButton name="menuMode" value="static" v-model="menuMode" @update:modelValue="handleSetMenuMode" inputId="static" />
                                <label for="static">{{ t('Static') }}</label>
                            </div>

                            <div class="flex items-center gap-2 w-6/12">
                                <RadioButton name="menuMode" value="overlay" v-model="menuMode" @update:modelValue="handleSetMenuMode" inputId="overlay" />
                                <label for="overlay">{{ t('Overlay') }}</label>
                            </div>
                        </div>
                        <div class="flex">
                            <div class="flex items-center gap-2 w-6/12">
                                <RadioButton name="menuMode" value="slim" v-model="menuMode" @update:modelValue="handleSetMenuMode" inputId="slim" />
                                <label for="slim">{{ t('Slim') }}</label>
                            </div>
                            <div class="flex items-center gap-2 w-6/12">
                                <RadioButton name="menuMode" value="compact" v-model="menuMode" @update:modelValue="handleSetMenuMode" inputId="compact" />
                                <label for="compact">{{ t('Compact') }}</label>
                            </div>
                        </div>
                        <div class="flex">
                            <div class="flex items-center gap-2 w-6/12">
                                <RadioButton name="menuMode" value="reveal" v-model="menuMode" @update:modelValue="handleSetMenuMode" inputId="reveal" />
                                <label for="reveal">{{ t('Reveal') }}</label>
                            </div>
                            <div class="flex items-center gap-2 w-6/12">
                                <RadioButton name="menuMode" value="drawer" v-model="menuMode" @update:modelValue="handleSetMenuMode" inputId="drawer" />
                                <label for="drawer">{{ t('Drawer') }}</label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Drawer>
</template>

<style>
/* =====================================================
   CORE THEME VARIABLES
   ===================================================== */

:root {
    /* Base Theme Colors */
    --theme-card-bg: #ffffff;
    --theme-card-border: #e5e7eb;
    --theme-card-shadow: rgba(0, 0, 0, 0.1);
    --theme-card-shadow-hover: rgba(0, 0, 0, 0.15);
    
    --theme-text-primary: #111827;
    --theme-text-secondary: #6b7280;
    --theme-text-muted: #9ca3af;
    --theme-text-accent: var(--p-primary-600);
    
    --theme-bg-primary: #f9fafb;
    --theme-bg-secondary: #f3f4f6;
    --theme-bg-accent: #fafafa;
    
    --theme-border-light: #f3f4f6;
    --theme-border-medium: #e5e7eb;
    --theme-border-strong: #d1d5db;
    
    /* Status Colors */
    --theme-success: #10b981;
    --theme-success-bg: #ecfdf5;
    --theme-warning: #f59e0b;
    --theme-warning-bg: #fffbeb;
    --theme-error: #ef4444;
    --theme-error-bg: #fef2f2;
    
    /* Component Variables */
    --theme-stat-icon-bg: rgba(255, 255, 255, 0.25);
    --theme-stat-icon-border: rgba(255, 255, 255, 0.2);
    --theme-button-shadow: 0 2px 8px rgba(0, 60, 86, 0.2);
    --theme-button-shadow-hover: 0 4px 12px rgba(0, 60, 86, 0.3);
    --theme-button-secondary-text: var(--p-primary-600);
    --theme-button-text-color: var(--p-primary-600);
    --theme-button-text-hover-bg: rgba(0, 60, 86, 0.1);
    --theme-button-disabled-bg: #e5e7eb;
    --theme-button-disabled-border: #d1d5db;
    --theme-button-disabled-text: #9ca3af;
    --theme-transition: all 0.3s ease;
}

/* Dark Theme Overrides */
.app-dark {
    --theme-card-bg: var(--p-surface-800);
    --theme-card-border: var(--p-surface-700);
    --theme-card-shadow: rgba(0, 0, 0, 0.3);
    --theme-card-shadow-hover: rgba(0, 0, 0, 0.4);
    
    --theme-text-primary: var(--p-surface-100);
    --theme-text-secondary: var(--p-surface-300);
    --theme-text-muted: var(--p-surface-400);
    --theme-text-accent: var(--p-primary-400);
    
    --theme-bg-primary: var(--p-surface-950);
    --theme-bg-secondary: var(--p-surface-900);
    --theme-bg-accent: var(--p-surface-800);
    
    --theme-border-light: var(--p-surface-700);
    --theme-border-medium: var(--p-surface-600);
    --theme-border-strong: var(--p-surface-500);
    
    --theme-success: #22c55e;
    --theme-success-bg: rgba(34, 197, 94, 0.1);
    --theme-warning: #fbbf24;
    --theme-warning-bg: rgba(251, 191, 36, 0.1);
    --theme-error: #f87171;
    --theme-error-bg: rgba(248, 113, 113, 0.1);
    
    --theme-stat-icon-bg: rgba(255, 255, 255, 0.1);
    --theme-stat-icon-border: rgba(255, 255, 255, 0.1);
    --theme-button-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    --theme-button-shadow-hover: 0 4px 12px rgba(0, 0, 0, 0.4);
    --theme-button-secondary-text: var(--p-primary-400);
    --theme-button-text-color: var(--p-primary-400);
    --theme-button-text-hover-bg: rgba(59, 130, 246, 0.1);
    --theme-button-disabled-bg: var(--p-surface-700);
    --theme-button-disabled-border: var(--p-surface-600);
    --theme-button-disabled-text: var(--p-surface-400);
}

/* =====================================================
   SMOOTH TRANSITIONS
   ===================================================== */

*,
*::before,
*::after {
    transition: 
        background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1),
        border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1),
        color 0.3s cubic-bezier(0.4, 0, 0.2, 1),
        box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1),
        opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* =====================================================
   COMPONENT STYLING
   ===================================================== */

/* =====================================================
   INTERACTIVE STATES WITH PRIMARY COLORS
   ===================================================== */

/* Router Link Active State */
.router-link-exact-active,
.bg-surface-200\/75 {
    background-color: var(--primary-500) !important;
    color: white !important;
}

.router-link-exact-active:hover {
    background-color: var(--primary-hover) !important;
}

/* Links */
a {
    color: var(--primary-500);
    transition: color 0.3s ease;
}

a:hover {
    color: var(--primary-hover);
}

a:active {
    color: var(--primary-active);
}

/* Avatar */
.p-avatar {
    background: var(--p-gradient-background) !important;
    color: #fff !important;
}

.p-avatar:hover {
    opacity: 0.9;
    transform: scale(1.05);
}

/* Buttons - General Interactive States */
button:not(:disabled) {
    cursor: pointer;
}

button:not(:disabled):hover {
    opacity: 0.95;
}

button:not(:disabled):active {
    transform: translateY(0px);
}

/* Input Focus States */
input:focus,
textarea:focus,
select:focus {
    border-color: var(--primary-focus) !important;
    outline: none;
}

/* Checkbox and Radio */
input[type="checkbox"]:checked,
input[type="radio"]:checked {
    background-color: var(--primary-500);
    border-color: var(--primary-500);
}

input[type="checkbox"]:checked:hover,
input[type="radio"]:checked:hover {
    background-color: var(--primary-hover);
    border-color: var(--primary-hover);
}

input[type="checkbox"]:focus,
input[type="radio"]:focus {
    outline: none;
}

/* Tabs Active State */
.p-tabview-nav-link.p-highlight {
    border-color: var(--primary-500) !important;
    color: var(--primary-500) !important;
}

.p-tabview-nav-link:not(.p-highlight):hover {
    border-color: var(--primary-hover) !important;
    color: var(--primary-hover) !important;
}

/* Menu Items */
.p-menuitem-link:hover {
    background-color: var(--primary-50) !important;
    color: var(--primary-hover) !important;
}

.p-menuitem-link:active {
    background-color: var(--primary-100) !important;
    color: var(--primary-active) !important;
}

.p-menuitem-link:focus {
    outline: none;
}

/* Pseudo Elements (::before and ::after) */
.primary-accent::before,
.primary-accent::after {
    background-color: var(--primary-before);
}

.primary-indicator::before {
    background-color: var(--primary-500);
}

.primary-indicator:hover::before {
    background-color: var(--primary-hover);
}

.primary-indicator:active::before {
    background-color: var(--primary-active);
}

/* Badges and Tags with Primary Color */
.p-badge-primary,
.p-tag-primary {
    background-color: var(--primary-500);
    color: white;
}

.p-badge-primary:hover,
.p-tag-primary:hover {
    background-color: var(--primary-hover);
}

/* Progress Bar */
.p-progressbar-value {
    background-color: var(--primary-500) !important;
}

/* Toggle/Switch */
.p-inputswitch.p-inputswitch-checked .p-inputswitch-slider {
    background-color: var(--primary-500) !important;
}

.p-inputswitch.p-inputswitch-checked:hover .p-inputswitch-slider {
    background-color: var(--primary-hover) !important;
}

.p-inputswitch.p-inputswitch-checked:focus .p-inputswitch-slider {
    outline: none;
}

/* Slider */
.p-slider-handle {
    background-color: var(--primary-500) !important;
    border-color: var(--primary-500) !important;
}

.p-slider-handle:hover {
    background-color: var(--primary-hover) !important;
    border-color: var(--primary-hover) !important;
}

.p-slider-range {
    background-color: var(--primary-500) !important;
}

/* Selection States */
.p-highlight {
    background-color: var(--primary-500) !important;
    color: white !important;
}

.p-highlight:hover {
    background-color: var(--primary-hover) !important;
}

/* Data Table Row Selection */
.p-datatable .p-datatable-tbody > tr.p-highlight {
    background-color: var(--primary-100) !important;
    color: var(--primary-700) !important;
}

.p-datatable .p-datatable-tbody > tr.p-highlight:hover {
    background-color: var(--primary-200) !important;
}

/* Ripple Effect */
.p-ink {
    background-color: var(--primary-500) !important;
    opacity: 0.2;
}

/* SelectButton Active State - Fix for Settings Panel */
:deep(.p-selectbutton .p-button.p-highlight) {
    background: var(--primary-500) !important;
    border-color: var(--primary-500) !important;
    color: white !important;
    box-shadow: 0 0 0 2px var(--primary-500) !important;
}

:deep(.p-selectbutton .p-button.p-highlight:hover) {
    background: var(--primary-hover) !important;
    border-color: var(--primary-hover) !important;
    box-shadow: 0 0 0 2px var(--primary-hover) !important;
}

:deep(.p-selectbutton .p-button:not(.p-highlight)) {
    background: transparent !important;
    border-color: var(--p-surface-300) !important;
    color: var(--p-text-color) !important;
}

:deep(.p-selectbutton .p-button:not(.p-highlight):hover) {
    background: var(--primary-50) !important;
    border-color: var(--primary-300) !important;
    color: var(--primary-600) !important;
}

/* Dark mode adjustments for SelectButton */
.app-dark :deep(.p-selectbutton .p-button:not(.p-highlight)) {
    border-color: var(--p-surface-600) !important;
}

.app-dark :deep(.p-selectbutton .p-button:not(.p-highlight):hover) {
    background: var(--primary-900) !important;
    border-color: var(--primary-600) !important;
    color: var(--primary-300) !important;
}

/* Stat Icon Container */
.stat-icon-container {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s ease;
    background: var(--theme-stat-icon-bg);
    backdrop-filter: blur(10px);
    border: 1px solid var(--theme-stat-icon-border);
}

/* Scrollbar Styling */
::-webkit-scrollbar {
    width: 6px;
}

::-webkit-scrollbar-track {
    background: var(--theme-bg-secondary);
}

::-webkit-scrollbar-thumb {
    background: var(--theme-border-strong);
    border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
    background: var(--theme-text-muted);
}

/* =====================================================
   PRIMEVUE BUTTON OVERRIDES
   ===================================================== */

/* Primary Button (with gradient) */
:deep(.p-button),
:deep(.p-button-primary) {
    background: var(--button-primary-bg) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    color: #ffffff !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 2px 8px rgba(30, 64, 55, 0.2) !important;
}

:deep(.p-button:hover),
:deep(.p-button-primary:hover) {
    background: var(--button-primary-hover) !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(30, 64, 55, 0.3) !important;
}

:deep(.p-button:active),
:deep(.p-button-primary:active) {
    background: var(--primary-active) !important;
    transform: translateY(0) !important;
    box-shadow: 0 2px 8px rgba(30, 64, 55, 0.2) !important;
}

:deep(.p-button:focus),
:deep(.p-button-primary:focus) {
    outline: none !important;
}

/* Success Button */
:deep(.p-button-success) {
    background: var(--button-success-bg) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    color: #ffffff !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2) !important;
}

:deep(.p-button-success:hover) {
    background: var(--button-success-hover) !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3) !important;
}

/* Info Button */
:deep(.p-button-info) {
    background: var(--button-info-bg) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    color: #ffffff !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2) !important;
}

:deep(.p-button-info:hover) {
    background: var(--button-info-hover) !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
}

/* Warning Button */
:deep(.p-button-warning) {
    background: var(--button-warning-bg) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    color: #ffffff !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2) !important;
}

:deep(.p-button-warning:hover) {
    background: var(--button-warning-hover) !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3) !important;
}

/* Help Button */
:deep(.p-button-help) {
    background: var(--button-help-bg) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    color: #ffffff !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 2px 8px rgba(139, 92, 246, 0.2) !important;
}

:deep(.p-button-help:hover) {
    background: var(--button-help-hover) !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3) !important;
}

/* Danger Button */
:deep(.p-button-danger) {
    background: var(--button-danger-bg) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    color: #ffffff !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.2) !important;
}

:deep(.p-button-danger:hover) {
    background: var(--button-danger-hover) !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3) !important;
}

/* Secondary & Outlined Buttons */
:deep(.p-button-secondary),
:deep(.p-button-outlined) {
    background: transparent !important;
    border: 1px solid var(--primary-500) !important;
    color: var(--primary-500) !important;
    transition: all 0.3s ease !important;
}

:deep(.p-button-secondary:hover),
:deep(.p-button-outlined:hover) {
    background: var(--primary-hover) !important;
    border-color: var(--primary-hover) !important;
    color: #ffffff !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(30, 64, 55, 0.2) !important;
}

:deep(.p-button-secondary:active),
:deep(.p-button-outlined:active) {
    background: var(--primary-active) !important;
    border-color: var(--primary-active) !important;
    transform: translateY(0) !important;
}

:deep(.p-button-secondary:focus),
:deep(.p-button-outlined:focus) {
    outline: none !important;
}

/* Text Buttons */
:deep(.p-button-text) {
    background: transparent !important;
    border: none !important;
    color: var(--primary-500) !important;
    transition: all 0.3s ease !important;
}

:deep(.p-button-text:hover) {
    background: var(--primary-50) !important;
    color: var(--primary-hover) !important;
    transform: translateY(-1px) !important;
}

:deep(.p-button-text:active) {
    background: var(--primary-100) !important;
    color: var(--primary-active) !important;
    transform: translateY(0) !important;
}

:deep(.p-button-text:focus) {
    outline: none !important;
}

/* Link Buttons */
:deep(.p-button-link) {
    background: transparent !important;
    border: none !important;
    color: var(--primary-500) !important;
    text-decoration: underline !important;
    transition: all 0.3s ease !important;
}

:deep(.p-button-link:hover) {
    color: var(--primary-hover) !important;
    transform: translateY(-1px) !important;
}

:deep(.p-button-link:active) {
    color: var(--primary-active) !important;
    transform: translateY(0) !important;
}

:deep(.p-button-link:focus) {
    outline: none !important;
}

/* Disabled State - All Button Types */
:deep(.p-button:disabled),
:deep(.p-button-primary:disabled),
:deep(.p-button-success:disabled),
:deep(.p-button-info:disabled),
:deep(.p-button-warning:disabled),
:deep(.p-button-help:disabled),
:deep(.p-button-danger:disabled),
:deep(.p-button-secondary:disabled),
:deep(.p-button-outlined:disabled),
:deep(.p-button-text:disabled),
:deep(.p-button-link:disabled) {
    background: #e5e7eb !important;
    border-color: #d1d5db !important;
    color: #9ca3af !important;
    cursor: not-allowed !important;
    transform: none !important;
    box-shadow: none !important;
    opacity: 0.6 !important;
}
</style>
